import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Collapse,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { trackFAQSectionOpened, trackFAQViewed } from "src/Utils/Mixpanel/VendorPortalEvents";
import disputedEntriesPdf from "./Disputed Entries Guide.pdf";
import introductionPdf from "./Introduction Vendor Portal.pdf";
import invoiceRejectionPdf from "./Invoice Rejection.pdf";
import ledgerSummaryPdf from "./Ledger Summary.pdf";
import raiseOtherIssuesPdf from "./Raise Other Issues.pdf";
import reconciliationPdf from "./Reconciliation.pdf";

interface FaqItem {
  question: string;
  answer: string;
}

interface VideoLink {
  title: string;
  url: string;
  description?: string;
  embedUrl?: string; // when provided, renders an inline iframe player
}

interface DocumentLink {
  title: string;
  url?: string;
  file?: File;
  description?: string;
  fileType?: string; // e.g. "PDF", "XLSX"
}

interface PageSegmentFaq {
  segmentTitle: string;
  faqs: FaqItem[];
  videos: VideoLink[];
  documents: DocumentLink[];
}

// ─── Data ────────────────────────────────────────────────────────────────────

const overallFaqs: FaqItem[] = [
  {
    question: "How do I access the Reconciliation module on the Vendor Portal?",
    answer:
      "Log in to the Instamart Vendor Portal and click Instamart Ledger Recon from the left-hand menu. If the page doesn't open, ensure pop-ups are enabled in your browser for the portal URL.",
  },
  {
    question: "What should I do if the Reconciliation or Disputed Entries page doesn't open?",
    answer: "Your browser may be blocking pop-ups. Enable pop-ups for the Instamart Vendor Portal and try again.",
  },
  {
    question: "I received a Balance Confirmation alert — what does it mean?",
    answer:
      "It indicates that Instamart has shared your balance. You must confirm if it matches your records or upload your own ledger if there's a mismatch.",
  },
  {
    question: "What should I do if my balance does not match Instamart's records?",
    answer:
      "Select Not Confirm Balance, enter your Closing Balance, upload your Ledger, and submit the details for Instamart's review.",
  },
  {
    question: "Why was my Balance Confirmation rejected?",
    answer:
      "If your submission is rejected, check the rejection reason in the alert, make the necessary corrections, and re-upload your response.",
  },
];

const overallVideos: VideoLink[] = [
  {
    title: "Vendor Portal Overview",
    url: "https://youtu.be/pcLwdauMSQ0",
    description: "A walkthrough of the portal and its main features.",
  },
  {
    title: "Vendor Self Serve Portal Features",
    url: "https://nakadindia-my.sharepoint.com/:v:/g/personal/admin_nakad_co/IQCf-8K3z1alRIIJrgnzM5n_AWdqOUUoCoLCI_RtmLZyH-Q?e=ND2mW0",
    embedUrl:
      "https://nakadindia-my.sharepoint.com/:v:/g/personal/admin_nakad_co/IQCf-8K3z1alRIIJrgnzM5n_AWdqOUUoCoLCI_RtmLZyH-Q?e=ND2mW0",
    description: "A walkthrough of the Vendor Self Serve Portal features.",
  },
];

const overallDocuments: DocumentLink[] = [
  {
    title: "Introduction Vendor Portal",
    url: introductionPdf,
    description: "Access and balance confirmation guide.",
    fileType: "PDF",
  },
];

const pageSegmentFaqs: PageSegmentFaq[] = [
  {
    segmentTitle: "Ledger Summary",
    faqs: [
      {
        question: "Why is the Ledger Summary blank for the selected dates?",
        answer:
          "Ledger data is only available up to yesterday (Today – 1). Selecting today or future dates will show no data.",
      },
      {
        question: "From when can I check the data of the Instamart ledger?",
        answer: "Ledger data is available from October 2021.",
      },
      {
        question: "I am getting an error while downloading the ledger. What should I do?",
        answer: "Reduce the date range and try again. Large data volumes may exceed Excel limits.",
      },
      {
        question: "Can I download PR/RTV or Promo-related proofs from Ledger Summary?",
        answer:
          "No, Ledger Summary is to provide you with the ledger entries of both the AP and AR sides of Instamart. No other proofs will be available.",
      },
      {
        question: "Can I check the original associated Invoice Number of PR/RTV?",
        answer:
          'Yes, the associated invoice number of PR/RTV can be found in the "Invoice Number" column of the ledger pertaining to PR/RTV entry.',
      },
    ],
    videos: [
      {
        title: "How to Read Your Ledger Summary",
        url: "https://youtu.be/pcLwdauMSQ0",
        description: "Step-by-step guide to understanding your ledger data.",
      },
      {
        title: "Vendor Self Serve Portal Features",
        url: "https://nakadindia-my.sharepoint.com/:v:/g/personal/admin_nakad_co/IQCf-8K3z1alRIIJrgnzM5n_AWdqOUUoCoLCI_RtmLZyH-Q?e=ND2mW0",
        embedUrl:
          "https://nakadindia-my.sharepoint.com/:v:/g/personal/admin_nakad_co/IQCf-8K3z1alRIIJrgnzM5n_AWdqOUUoCoLCI_RtmLZyH-Q?e=ND2mW0",
        description: "A walkthrough of the Vendor Self Serve Portal features.",
      },
    ],
    documents: [
      {
        title: "Ledger Summary",
        url: ledgerSummaryPdf,
        description: "Guide to understanding your ledger data on the Vendor Portal.",
        fileType: "PDF",
      },
    ],
  },
  {
    segmentTitle: "Invoice Rejection",
    faqs: [
      {
        question: "How do I access the Rejected Invoice Module?",
        answer: "Log in to the Vendor Portal → Go to Invoice Report → Click Rejected Invoice Module.",
      },
      {
        question: "What does 'In Progress' mean?",
        answer: "The invoice is in progress — action may be needed either from the Vendor or Instamart.",
      },
      {
        question: "What does 'Pending Approval' mean?",
        answer: "Instamart is reviewing the submitted response for approval or rejection.",
      },
      {
        question: "What does 'Invoice Processing' mean?",
        answer: "The invoice has been approved and is being updated in Instamart's system.",
      },
      {
        question: "What's the difference between 'Not Posted' and 'Posted'?",
        answer:
          "Not Posted: Entries still under review or processing. Posted: Invoice has been fully accepted and updated.",
      },
      {
        question: "When do I need to take action?",
        answer: "Only if the entry is marked as Invoice Copy Required or New Invoice Required under In Progress.",
      },
      {
        question: "How do I take action?",
        answer: "Click Take Action → Upload the required document → Click Submit.",
      },
      {
        question: "What kind of documents should I upload?",
        answer: "Upload a valid invoice copy or a fresh invoice as requested — make sure it's clear and complete.",
      },
      {
        question: "What is the file size or format limit for uploads?",
        answer: "Files must be under 50 MB per file and in PDF, JPG, or PNG format.",
      },
      {
        question: "Can I take action on all entries in 'In Progress'?",
        answer:
          "No. You can take action only on entries under the category of Invoice Copy Required or New Invoice Required.",
      },
      {
        question: "What if the entry is under 'All' in In Progress?",
        answer:
          "It's for visibility to find the rejected invoice; check the subcategory to know if action is required.",
      },
      {
        question: "What happens after I submit?",
        answer: "The entry moves to Pending Approval, then to Invoice Processing, and finally to Posted once complete.",
      },
      {
        question: "Can I edit or remove a file after submitting it?",
        answer:
          "No. Once submitted, files cannot be deleted. If there's an error, wait for Instamart to reject the entry, then re-upload.",
      },
      {
        question: "How will I know if my action was accepted?",
        answer:
          "Once approved, the entry will move from Pending Approval to Invoice Processing, and finally to Posted.",
      },
      {
        question: "What if I uploaded the wrong document?",
        answer: "You can then take action again and upload the correct document.",
      },
    ],
    videos: [
      {
        title: "Managing Rejected Invoices",
        url: "https://youtu.be/c5YvSzM58bw",
        description: "Learn how to view, understand, and act on rejected invoices.",
      },
      {
        title: "Rejected Invoice Portal Walkthrough",
        url: "https://nakadindia-my.sharepoint.com/:v:/g/personal/admin_nakad_co/IQBHuUsVFtbfTqoJZm9KIvbRARdsoOY7GUvNjmPOmScMgb0?e=GmZ9i4",
        embedUrl:
          "https://nakadindia-my.sharepoint.com/:v:/g/personal/admin_nakad_co/IQBHuUsVFtbfTqoJZm9KIvbRARdsoOY7GUvNjmPOmScMgb0?e=GmZ9i4",
        description: "Step-by-step walkthrough of the Rejected Invoice module on the Vendor Portal.",
      },
    ],
    documents: [
      {
        title: "Invoice Rejection",
        url: invoiceRejectionPdf,
        description: "Full guide on invoice rejection and recommended actions.",
        fileType: "PDF",
      },
    ],
  },
  {
    segmentTitle: "Reconciliation Summary",
    faqs: [
      {
        question: "How can I check my reconciliation?",
        answer:
          "Open the Reconciliation tab and find the summary and output of reconciliation for the specified time period.",
      },
      {
        question: "I can't see any reconciliation on the portal. What should I do?",
        answer:
          "This means that Instamart has either not started the reconciliation yet or it is still in progress. You can check the Recon Status column for updates and wait until Instamart completes the reconciliation.",
      },
      {
        question: "How can I download the reconciliation report?",
        answer:
          "Once reconciliation is completed, click the Download Reconciliation button to get the detailed report.",
      },
    ],
    videos: [
      {
        title: "Understanding Reconciliation Summary",
        url: "https://youtu.be/pcLwdauMSQ0",
        description: "A walkthrough to download the reconciliation performed by Instamart.",
      },
      {
        title: "Vendor Self Serve Portal Features",
        url: "https://nakadindia-my.sharepoint.com/:v:/g/personal/admin_nakad_co/IQCf-8K3z1alRIIJrgnzM5n_AWdqOUUoCoLCI_RtmLZyH-Q?e=ND2mW0",
        embedUrl:
          "https://nakadindia-my.sharepoint.com/:v:/g/personal/admin_nakad_co/IQCf-8K3z1alRIIJrgnzM5n_AWdqOUUoCoLCI_RtmLZyH-Q?e=ND2mW0",
        description: "A walkthrough of the Vendor Self Serve Portal features.",
      },
    ],
    documents: [
      {
        title: "Reconciliation",
        url: reconciliationPdf,
        description: "How to download reconciliation summary and annexures.",
        fileType: "PDF",
      },
    ],
  },
  {
    segmentTitle: "Recon Dispute Entries",
    faqs: [
      {
        question: "Where can I find disputes raised during reconciliation?",
        answer:
          "Go to Disputed Entries from the left-hand menu. You can filter results using the Select Organisation Name dropdown.",
      },
      {
        question: 'What do the categories under "Pending" mean?',
        answer:
          "Docs Pending: Instamart requires specific documents from you. Clarification Pending: Instamart has requested clarification or an explanation. In Progress by Instamart: Instamart is currently working on the issue. Booking Pending: You must book the transaction as per the uploaded documents.",
      },
      {
        question: 'What does "Resolved" mean in Disputed Entries?',
        answer: "It means the dispute is closed — either booked, reversed, or cancelled by Instamart or your team.",
      },
      {
        question: "How do I upload documents under Docs Pending?",
        answer:
          "Click View Details → Take Action for any specific entry, upload the required documents (PO, Invoice, POD, Payment Proof, or Credit Note details as asked in the Action column), and submit.",
      },
      {
        question: "What should I do when clarification is requested by Instamart?",
        answer:
          "Open the entry, review the remarks provided, upload supporting documents, and add a short clarification note in Vendor Remarks.",
      },
      {
        question: "How do I confirm once I've booked the entry in my books?",
        answer: "After updating your books, open the entry and select Mark as Booked to confirm the booking.",
      },
      {
        question: "Can I download disputed entries for offline review?",
        answer: "Yes. Click the Download icon in the Disputed Entries section to export the entries in Excel format.",
      },
      {
        question: "Can I filter entries to find specific cases?",
        answer: "Yes. Use the Filter button to filter by invoice number, CN/DN number, etc.",
      },
      {
        question: "Can I update multiple entries at once?",
        answer:
          "If enabled, use the Bulk Action feature to apply the same remark across multiple entries simultaneously.",
      },
      {
        question: "How often should I check the Disputed Entries section?",
        answer:
          "It is recommended to review it regularly to stay updated on any new requests or changes from Instamart.",
      },
      {
        question: "Can I mark the entry as booked after raising a dispute?",
        answer: "No. Once you raise a dispute, you must wait for Instamart's response and the next assigned action.",
      },
    ],
    videos: [
      {
        title: "Raising a Reconciliation Dispute",
        url: "https://youtu.be/pcLwdauMSQ0",
        description: "Step-by-step guide to submitting a dispute on a reconciliation gap.",
      },
      {
        title: "Vendor Self Serve Portal Features",
        url: "https://nakadindia-my.sharepoint.com/:v:/g/personal/admin_nakad_co/IQCf-8K3z1alRIIJrgnzM5n_AWdqOUUoCoLCI_RtmLZyH-Q?e=ND2mW0",
        embedUrl:
          "https://nakadindia-my.sharepoint.com/:v:/g/personal/admin_nakad_co/IQCf-8K3z1alRIIJrgnzM5n_AWdqOUUoCoLCI_RtmLZyH-Q?e=ND2mW0",
        description: "A walkthrough of the Vendor Self Serve Portal features.",
      },
    ],
    documents: [
      {
        title: "Disputed Entries Guide",
        url: disputedEntriesPdf,
        description: "Guide to raising and tracking disputes on reconciliation gaps.",
        fileType: "PDF",
      },
    ],
  },
  {
    segmentTitle: "Hold Resolution",
    faqs: [
      {
        question: "What types of holds are shown in the Hold Resolution section?",
        answer:
          "The Hold Resolution section shows four categories: GST Related Hold, PR/RTV Related Hold, BDPO Related Hold, and Ads Visibility Related Hold.",
      },
      {
        question: "What does 'Pending' mean in Hold Resolution?",
        answer:
          "Pending holds are amounts that are currently withheld by Instamart and require action or review before they can be released.",
      },
      {
        question: "What does 'Resolved' mean in Hold Resolution?",
        answer:
          "Resolved holds are those where the vendor action has been accepted and the hold has been released by Instamart Finance.",
      },
      {
        question: "Can I filter hold data by organisation?",
        answer:
          "Yes. Use the 'Select Organisation Name' dropdown at the top of the Hold Resolution page to filter data by a specific entity.",
      },
      {
        question: "The hold amount shows '—'. What does that mean?",
        answer: "A dash (—) means there are no entries for that hold category. No action is required.",
      },
      {
        question: "Where should I raise queries about my holds?",
        answer: "For any hold-related queries, please use the Raise Other Issues section on the portal.",
      },
    ],
    videos: [
      {
        title: "Vendor Self Serve Portal Features",
        url: "https://nakadindia-my.sharepoint.com/:v:/g/personal/admin_nakad_co/IQCf-8K3z1alRIIJrgnzM5n_AWdqOUUoCoLCI_RtmLZyH-Q?e=ND2mW0",
        embedUrl:
          "https://nakadindia-my.sharepoint.com/:v:/g/personal/admin_nakad_co/IQCf-8K3z1alRIIJrgnzM5n_AWdqOUUoCoLCI_RtmLZyH-Q?e=ND2mW0",
        description: "A walkthrough of the Vendor Self Serve Portal features including Hold Resolution.",
      },
    ],
    documents: [],
  },
  {
    segmentTitle: "Raise Other Issues",
    faqs: [
      {
        question: "When should I raise an Other Issue instead of a regular dispute?",
        answer:
          "Raise an issue when the case is not present in the reconciliation or ledger summary, such as a missing invoice or a PR/RTV issue outside the recon cycle.",
      },
      {
        question: "What types of Other Issues can I raise?",
        answer:
          "You can raise: Invoice Missing — when an invoice is not available in Ledger Summary or Rejected Invoices. PR / RTV Issue — when there is an issue related to Purchase Return or Return to Vendor entries.",
      },
      {
        question: "Where can I track the Other Issues I have raised?",
        answer:
          "Go to Raise Other Issues → Other Issues Dashboard. You will see a dashboard similar to the Recon Gap Entries view, with the same statuses and action flow.",
      },
    ],
    videos: [
      {
        title: "How to Raise an Other Issue",
        url: "https://youtu.be/pcLwdauMSQ0",
        description: "Learn when and how to use the Raise Other Issues feature.",
      },
      {
        title: "Vendor Self Serve Portal Features",
        url: "https://nakadindia-my.sharepoint.com/:v:/g/personal/admin_nakad_co/IQCf-8K3z1alRIIJrgnzM5n_AWdqOUUoCoLCI_RtmLZyH-Q?e=ND2mW0",
        embedUrl:
          "https://nakadindia-my.sharepoint.com/:v:/g/personal/admin_nakad_co/IQCf-8K3z1alRIIJrgnzM5n_AWdqOUUoCoLCI_RtmLZyH-Q?e=ND2mW0",
        description: "A walkthrough of the Vendor Self Serve Portal features.",
      },
    ],
    documents: [
      {
        title: "Raise Other Issues",
        url: raiseOtherIssuesPdf,
        description: "Guidelines on what information to include when reporting an issue.",
        fileType: "PDF",
      },
    ],
  },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

const FaqAccordionList: React.FC<{ faqs: FaqItem[] }> = ({ faqs }) => {
  const [expanded, setExpanded] = useState<string | false>(false);

  const handleChange = (panel: string, question: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
    if (isExpanded) trackFAQSectionOpened({ module: "faq", question });
  };

  return (
    <Stack spacing={1}>
      {faqs.map((faq, idx) => (
        <Accordion
          key={idx}
          expanded={expanded === `panel-${idx}`}
          onChange={handleChange(`panel-${idx}`, faq.question)}
          disableGutters
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "8px !important",
            "&:before": { display: "none" },
            "&.Mui-expanded": {
              borderColor: "rgba(84, 28, 76, 0.4)",
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: "#541c4c" }} />}
            sx={{
              px: 2.5,
              py: 0.5,
              "& .MuiAccordionSummary-content": { my: 1.5 },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
              <HelpOutlineIcon sx={{ color: "#541c4c", mt: 0.2, fontSize: 20, flexShrink: 0 }} />
              <Typography sx={{ fontWeight: 500, fontSize: 14, color: "rgba(0,0,0,0.87)", lineHeight: 1.5 }}>
                {faq.question}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 2.5, pt: 0, pb: 2, ml: 4 }}>
            <Typography sx={{ fontSize: 14, color: "rgba(0,0,0,0.6)", lineHeight: 1.7 }}>{faq.answer}</Typography>
          </AccordionDetails>
        </Accordion>
      ))}
    </Stack>
  );
};

const VideoLinkList: React.FC<{ videos: VideoLink[] }> = ({ videos }) => {
  if (videos.length === 0) return null;

  return (
    <Box sx={{ mt: 2 }}>
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: 13,
          color: "rgba(0,0,0,0.5)",
          textTransform: "uppercase",
          letterSpacing: 0.8,
          mb: 1.5,
        }}
      >
        Tutorial Videos
      </Typography>
      <Stack spacing={2}>
        {videos.map(
          (video, idx) =>
            video.embedUrl ? (
              <Box
                key={idx}
                sx={{
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  overflow: "hidden",
                  backgroundColor: "#000",
                }}
              >
                <Box
                  component="video"
                  controls
                  preload="metadata"
                  controlsList="nodownload"
                  src={`${video.embedUrl}&download=1`}
                  sx={{ display: "block", width: "100%", maxHeight: 420, backgroundColor: "#000" }}
                />
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1.5, backgroundColor: "#fdf6fc" }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      backgroundColor: "rgba(84, 28, 76, 0.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <OndemandVideoIcon sx={{ color: "#541c4c", fontSize: 18 }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: 13, color: "#541c4c", lineHeight: 1.4 }}>
                      {video.title}
                    </Typography>
                    {video.description && (
                      <Typography sx={{ fontSize: 12, color: "rgba(0,0,0,0.5)", lineHeight: 1.4, mt: 0.25 }}>
                        {video.description}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            ) : null
          /* <Link
              key={idx}
              href={video.url}
              target="_blank"
              rel="noopener noreferrer"
              underline="none"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                p: 1.5,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: "#fdf6fc",
                transition: "all 0.2s",
                "&:hover": {
                  borderColor: "#541c4c",
                  backgroundColor: "rgba(84, 28, 76, 0.04)",
                  boxShadow: "0 2px 8px rgba(84, 28, 76, 0.1)",
                },
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  backgroundColor: "rgba(84, 28, 76, 0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <OndemandVideoIcon sx={{ color: "#541c4c", fontSize: 20 }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: 13, color: "#541c4c", lineHeight: 1.4 }}>
                  {video.title}
                </Typography>
                {video.description && (
                  <Typography sx={{ fontSize: 12, color: "rgba(0,0,0,0.5)", lineHeight: 1.4, mt: 0.25 }}>
                    {video.description}
                  </Typography>
                )}
              </Box>
            </Link> */
        )}
      </Stack>
    </Box>
  );
};

const DocumentLinkList: React.FC<{ documents: DocumentLink[] }> = ({ documents }) => {
  if (!documents || documents.length === 0) return null;

  const handleOpen = (doc: DocumentLink) => {
    if (doc.file) {
      const objectUrl = URL.createObjectURL(doc.file);
      window.open(objectUrl, "_blank", "noopener,noreferrer");
    } else if (doc.url) {
      window.open(doc.url, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: 13,
          color: "rgba(0,0,0,0.5)",
          textTransform: "uppercase",
          letterSpacing: 0.8,
          mb: 1.5,
        }}
      >
        Reference Documents
      </Typography>
      <Stack spacing={1.5}>
        {documents.map((doc, idx) => (
          <Box
            key={idx}
            onClick={() => handleOpen(doc)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              p: 1.5,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              backgroundColor: "#f6fbff",
              transition: "all 0.2s",
              cursor: "pointer",
              "&:hover": {
                borderColor: "#1565c0",
                backgroundColor: "rgba(21, 101, 192, 0.04)",
                boxShadow: "0 2px 8px rgba(21, 101, 192, 0.1)",
              },
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                backgroundColor: "rgba(21, 101, 192, 0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <DescriptionOutlinedIcon sx={{ color: "#1565c0", fontSize: 20 }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 600, fontSize: 13, color: "#1565c0", lineHeight: 1.4 }}>
                {doc.title}
              </Typography>
              {doc.description && (
                <Typography sx={{ fontSize: 12, color: "rgba(0,0,0,0.5)", lineHeight: 1.4, mt: 0.25 }}>
                  {doc.description}
                </Typography>
              )}
            </Box>
            <Typography sx={{ fontSize: 11, color: "#1565c0", fontWeight: 500, flexShrink: 0, opacity: 0.7 }}>
              View ›
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const FaqVendorPortal: React.FC = () => {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  React.useEffect(() => {
    trackFAQViewed();
  }, []);

  const toggleSection = (key: string) => {
    setExpandedSection((prev) => {
      if (prev !== key) trackFAQSectionOpened({ module: key, question: key });
      return prev === key ? null : key;
    });
  };

  return (
    <Box sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 }, maxWidth: "100%" }}>
      {/* ── Overall FAQs ── */}
      <Box sx={{ mb: 5 }}>
        <Box
          onClick={() => toggleSection("general")}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: expandedSection === "general" ? 0.75 : 0,
            cursor: "pointer",
            userSelect: "none",
            py: 1,
            px: 1.5,
            borderRadius: 2,
            "&:hover": { backgroundColor: "rgba(84, 28, 76, 0.04)" },
          }}
        >
          <KeyboardArrowDownIcon
            sx={{
              color: "#541c4c",
              fontSize: 22,
              transition: "transform 0.25s",
              transform: expandedSection === "general" ? "rotate(0deg)" : "rotate(-90deg)",
              flexShrink: 0,
            }}
          />
          <Typography variant="h5" sx={{ fontWeight: 700, color: "rgba(0,0,0,0.87)" }}>
            Frequently Asked Questions
          </Typography>
          <Chip
            label="General"
            size="small"
            sx={{ backgroundColor: "rgba(84, 28, 76, 0.1)", color: "#541c4c", fontWeight: 600 }}
          />
        </Box>

        <Collapse in={expandedSection === "general"} timeout="auto">
          <Typography sx={{ fontSize: 14, color: "rgba(0,0,0,0.5)", mb: 3, px: 1.5 }}>
            Common questions about the Vendor Portal platform.
          </Typography>

          <FaqAccordionList faqs={overallFaqs} />

          {/* Overall video links */}
          <VideoLinkList videos={overallVideos} />

          {/* Overall document links */}
          <DocumentLinkList documents={overallDocuments} />
        </Collapse>
      </Box>

      <Divider sx={{ mb: 5 }}>
        <Typography
          sx={{ fontSize: 12, color: "rgba(0,0,0,0.4)", fontWeight: 500, textTransform: "uppercase", letterSpacing: 1 }}
        >
          Page-Specific Help
        </Typography>
      </Divider>

      {/* ── Per-Page Segment FAQs ── */}
      <Stack spacing={5}>
        {pageSegmentFaqs.map((segment, sIdx) => (
          <Box key={sIdx}>
            {/* Segment Header */}
            <Box
              onClick={() => toggleSection(`segment-${sIdx}`)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                mb: expandedSection === `segment-${sIdx}` ? 0.75 : 0,
                cursor: "pointer",
                userSelect: "none",
                py: 1,
                px: 1.5,
                borderRadius: 2,
                "&:hover": { backgroundColor: "rgba(84, 28, 76, 0.04)" },
              }}
            >
              <KeyboardArrowDownIcon
                sx={{
                  color: "#541c4c",
                  fontSize: 22,
                  transition: "transform 0.25s",
                  transform: expandedSection === `segment-${sIdx}` ? "rotate(0deg)" : "rotate(-90deg)",
                  flexShrink: 0,
                }}
              />
              <Typography variant="h6" sx={{ fontWeight: 700, color: "rgba(0,0,0,0.87)", fontSize: 18 }}>
                {segment.segmentTitle}
              </Typography>
              <Chip
                label={`${segment.faqs.length} FAQ${segment.faqs.length !== 1 ? "s" : ""}`}
                size="small"
                sx={{ backgroundColor: "rgba(84, 28, 76, 0.08)", color: "#541c4c", fontWeight: 600, fontSize: 11 }}
              />
            </Box>

            <Collapse in={expandedSection === `segment-${sIdx}`} timeout="auto">
              {/* FAQs */}
              <FaqAccordionList faqs={segment.faqs} />

              {/* Video links */}
              <VideoLinkList videos={segment.videos} />

              {/* Document links */}
              <DocumentLinkList documents={segment.documents} />
            </Collapse>
          </Box>
        ))}
      </Stack>

      {/* Footer note */}
      {/* <Box
        sx={{
          mt: 6,
          p: 3,
          borderRadius: 2,
          backgroundColor: "rgba(84, 28, 76, 0.04)",
          border: "1px solid rgba(84, 28, 76, 0.15)",
          textAlign: "center",
        }}
      >
        <Typography sx={{ fontSize: 14, color: "rgba(0,0,0,0.6)" }}>
          Still have questions?{" "}
          <Link href="mailto:support@nakad.co" sx={{ color: "#541c4c", fontWeight: 600 }}>
            Contact NAKAD Support
          </Link>
        </Typography>
      </Box> */}
    </Box>
  );
};

export default FaqVendorPortal;
